// src/sim/movement.ts — P1 character movement + facing constants (MOVE-04, D-15).
//
// Invariants:
//   - No side effects; returns new values; pure TS.
//   - No DOM, no Phaser, no timers (ESLint Plan 02 enforces).
//   - All constants flagged TODO(P5) for per-character stat-lookup replacement
//     (CHAR-03 in REQUIREMENTS.md — see D-14, D-15).
//
// Source: Phase 1 RESEARCH.md §Pattern 4 (Facing Lerp), CONTEXT.md D-13/D-15/D-18.

import type { MapData, Vec2 } from '../shared/types/map';
import { isWalkable } from './map/grid';
import { P2_DARK_SPEED_FLOOR } from './lighting/constants';
import { zoneCenter } from './map/zones';
import { P3_PLINTH_RING_RADIUS_PX } from './catch/constants';
import {
  circleOverlapsPolygon,
  circleOverlapsFence,
  circleOverlapsTrunk,
  circleInsideBoundary,
  closestPointOnSegment,
  polygonPushNormal,
  boundaryPushNormal,
  trunkPushNormal,
  FENCE_COLLISION_HALF_WIDTH,
} from './geometry';

/** Base walking speed in world units per second. TODO(P5): per-character stat from KV. */
export const P1_DEFAULT_BASE_SPEED = 144;

/** Facing turn rate in radians per second (≈ 690°/s; full 180° in ~262 ms). TODO(P5): per-character stat. */
export const P1_DEFAULT_TURN_RATE = 12;

/** Multiplier applied to base speed when Intent.actions.sprint is true. TODO(P5): per-character stat. */
export const P1_SPRINT_MULTIPLIER = 1.5;

// Re-export the dark-speed floor so callers can `import { P2_DARK_SPEED_FLOOR } from './movement'`
// alongside the P1_* constants — consistent home for "speed-related tunables".
export { P2_DARK_SPEED_FLOOR } from './lighting/constants';

/**
 * Dark-is-slower speed multiplier (VIS-07, D-08).
 * light=0 → 0.6× (speed floor); light=1 → 1.0×. Linear interpolation between.
 * Above the >20% variance threshold Pitfall #17 requires so the mechanic is felt.
 *
 * Defensively clamps the input to [0,1] — lightLevelAt already returns in range,
 * but a wrong-shaped caller would otherwise overshoot the floor or ceiling.
 */
export function lightSpeedRamp(lightLevel: number): number {
  const clamped = Math.max(0, Math.min(1, lightLevel));
  return P2_DARK_SPEED_FLOOR + (1 - P2_DARK_SPEED_FLOOR) * clamped;
}

/**
 * Visual radius of the P1 placeholder character, in world pixels. Shared with the
 * render-layer CharacterSprite (src/render/sprites/character-sprite.ts
 * CHARACTER_RADIUS = 8). The sim owns this as the AUTHORITATIVE hit-radius used
 * for wall clamping in `clampMotionAgainstWalls` — sim must be Phaser-free so we
 * cannot import from the render layer; render instead duplicates the literal and
 * both constants must move together. TODO(P5): per-character stat from KV.
 */
export const P1_CHARACTER_RADIUS = 8;

/**
 * Shortest-angle lerp between two radians, rate-limited per tick.
 * Normalises the delta into (-π, π] so a 170° → -170° flick takes the short way
 * through ±π, not the long way through 0 (Pitfall #39).
 */
export function lerpFacing(current: number, target: number, dt: number, rate: number): number {
  let delta = target - current;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta <= -Math.PI) delta += 2 * Math.PI;
  const maxStep = rate * dt;
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
}

/**
 * Sim-side axis-separated wall clamping for a circle of radius `radius` moving
 * from `from` to `to` across `mapData.grid`.
 *
 * Pure — no side effects. Deterministic given inputs. Safe to call from
 * tick() under the FOUND-07 determinism guard.
 *
 * ALGORITHM (axis-separated "slide-into-wall"):
 *   1. Attempt x-motion alone: check if a circle at (to.x, from.y) would
 *      overlap any non-walkable tile. If not, accept to.x; else revert to from.x.
 *   2. Attempt y-motion alone using the already-clamped x: same check at
 *      (nextX, to.y). If not overlapping, accept to.y; else revert to from.y.
 *
 * This is the standard "slide along walls" behaviour: walking diagonally into
 * a wall lets the component parallel to the wall continue while the normal
 * component is stopped.
 *
 * CIRCLE vs TILE check: rasterise the axis-aligned bounding box of the circle
 * into tile-space (leftTile..rightTile, topTile..bottomTile) and reject if ANY
 * covered tile is non-walkable. This over-approximates a circle by its AABB,
 * which is acceptable for this project — the placeholder wall ring is axis-
 * aligned and one-tile-thick, and the family-scale game does not need
 * pixel-perfect circle-vs-tile math in v1.
 *
 * Why sim-side (not Phaser Arcade collider):
 *   Plan 01-07 Task 3 UAT observed that Phaser's collider can't resolve
 *   collisions when the render teleports the character via setPosition every
 *   frame: the sim produces "walked into wall" positions, physics pushes out,
 *   sim re-advances on the next tick because it never reads back. Clamping
 *   here keeps the sim authoritative AND deterministic, and the Arcade
 *   collider remains as a safety net for edge cases (e.g. future spawn
 *   placements on a tile boundary).
 */
export function clampMotionAgainstWalls(
  from: Vec2,
  to: Vec2,
  radius: number,
  mapData: MapData,
): Vec2 {
  // Axis-separated strict blocking with a stuck-origin escape clause.
  //
  // Normal case (from NOT overlapping any obstacle): motion is allowed on an
  // axis iff the destination circle overlaps zero obstacles on that axis.
  // Escape-hatch case (from is already inside a shape e.g. a mis-placed
  // spawn or a physics push-in): motion is allowed unconditionally so the
  // character can move out. This prevents the degenerate "stuck forever"
  // failure observed on Plan 01-07 Task 5 UAT v3 when spawn landed on a
  // tile-boundary and the radius-16 circle overlapped a wall from tick 1.
  //
  // Phase 08.1 Item 1: POLYGON MODE vs TILE-GRID MODE.
  //   When mapData.walls is present (real Mouchao map, 31 authored polygons),
  //   movement resolution uses circle-vs-polygon precision for walls, obstacles,
  //   fences, and circle-vs-ellipse for tree trunks.  This stops entities exactly
  //   at the drawn wall edge rather than at the over-blocking rasterised tile grid.
  //   When mapData.walls is absent (sim-test minimal MapData), falls back to the
  //   existing tile-grid clamp (movementGrid) — backward compatible.
  //
  // Plinth ring is always checked (special circular obstacle at zoneCenter('plinth')
  // with radius P3_PLINTH_RING_RADIUS_PX). Debug session cascade-prevention (2026-04-28).
  const ring = getPlinthRing(mapData);
  const hasWalls = (mapData.walls?.length ?? 0) > 0;

  const blocks = (x: number, y: number): boolean => {
    // Plinth ring (always checked regardless of mode).
    if (ring !== null && circleOverlapsRing(x, y, radius, ring.center, ring.radius)) {
      return true;
    }

    if (hasWalls) {
      // POLYGON MODE: check wall polygons, obstacle polygons, fence segments,
      // and trunk ellipses. The tile movementGrid is NOT used here so wall
      // polygons at real angles (~24°) don't over-block.

      // Out-of-bounds boundary (Phase 08.1 Item 3) — the character circle must
      // stay fully INSIDE the authored playable-area polygon. Blocks the moment
      // the circle would cross the invisible edge; the tangent-slide resolver
      // then glides the player along it. Absent boundary → unbounded.
      if (mapData.boundary !== undefined && mapData.boundary.length >= 3) {
        if (!circleInsideBoundary(x, y, radius, mapData.boundary)) return true;
      }

      // Wall polygons — building walls at real authoring angles.
      for (const wall of mapData.walls!) {
        if (circleOverlapsPolygon(x, y, radius, wall.vertices)) return true;
      }

      // Obstacle polygons — benches, urns, low walls, planters.
      for (const obs of mapData.obstacles ?? []) {
        if (circleOverlapsPolygon(x, y, radius, obs.vertices)) return true;
      }

      // Fence segments — stone walls, wire fences, hedges (capsule collision).
      for (const fence of mapData.fences ?? []) {
        const pts = fence.points;
        for (let i = 0; i + 1 < pts.length; i++) {
          if (
            circleOverlapsFence(
              x, y, radius,
              pts[i]!.x, pts[i]!.y,
              pts[i + 1]!.x, pts[i + 1]!.y,
              FENCE_COLLISION_HALF_WIDTH,
            )
          ) {
            return true;
          }
        }
      }

      // Trunk ellipses — sized to 22% of canopy (same formula as tilemap-loader).
      for (const tree of mapData.trees ?? []) {
        if (
          circleOverlapsTrunk(
            x, y, radius,
            tree.cx, tree.cy, tree.trunkRx, tree.trunkRy,
          )
        ) {
          return true;
        }
      }

      return false;
    } else {
      // TILE-GRID MODE: existing rasterised collision (walls + obstacles + fences
      // rasterised into movementGrid; trunks rasterised into base grid).
      // Backward-compatible with sim-test minimal MapData that has no polygon arrays.
      return circleOverlapsWall(x, y, radius, mapData);
    }
  };

  // POLYGON MODE (real map): resolve with tangent-projection sliding so entities
  // slide smoothly along walls at ANY angle (fixes the diagonal-wall "jump" that
  // axis-separated resolution produces on the ~24°-authored Mouchao walls).
  // TILE-GRID MODE (sim-test maps, golden-hash replay fixture): keep the existing
  // axis-separated + frictionless-slide behaviour byte-identical.
  if (hasWalls) {
    return slideAgainstShapes(from, to, radius, mapData, ring, blocks);
  }
  return axisSeparatedClamp(from, to, blocks);
}

/**
 * Axis-separated "slide-into-wall" clamp with a frictionless full-speed slide.
 *
 * This is the ORIGINAL clampMotionAgainstWalls resolution, extracted verbatim so
 * tile-grid mode (and the polygon-mode fallback) stay byte-identical to the
 * pre-2026-07-03 behaviour — preserving the golden-hash replay fixture (no walls)
 * and the movement-clamp.test.ts locks.
 *
 * 1. Attempt x-motion alone; revert to from.x if the destination circle blocks.
 * 2. Attempt y-motion alone using the already-clamped x.
 * 3. Frictionless slide (movement-feel fix, 2026-06-12): when EXACTLY ONE axis is
 *    blocked, redistribute the FULL intended step length onto the free axis so
 *    sliding keeps full speed (Hotline-Miami-style). The boosted point is
 *    re-checked so we never slide INTO a wall; per-tick step is a few px so there
 *    is no tunnelling. Math.sqrt (not Math.hypot) keeps replay determinism.
 *
 * The escape-hatch (origin already inside a shape → motion allowed unconditionally
 * so a mis-placed spawn / physics push-in can move out) is preserved via `stuck`.
 */
function axisSeparatedClamp(
  from: Vec2,
  to: Vec2,
  blocks: (x: number, y: number) => boolean,
): Vec2 {
  const stuck = blocks(from.x, from.y);
  const xBlocked = !stuck && blocks(to.x, from.y);
  let nextX = xBlocked ? from.x : to.x;
  const yBlocked = !stuck && blocks(nextX, to.y);
  let nextY = yBlocked ? from.y : to.y;

  if (!stuck && xBlocked !== yBlocked) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const stepLen = Math.sqrt(dx * dx + dy * dy);
    if (xBlocked && dy !== 0) {
      const slideY = from.y + Math.sign(dy) * stepLen;
      if (!blocks(nextX, slideY)) nextY = slideY;
    } else if (yBlocked && dx !== 0) {
      const slideX = from.x + Math.sign(dx) * stepLen;
      if (!blocks(slideX, nextY)) nextX = slideX;
    }
  }
  return { x: nextX, y: nextY };
}

/**
 * Tangent-projection slide for POLYGON MODE (real Mouchao map).
 *
 * Given an intended motion from → to that ends inside a solid shape, project the
 * motion onto the wall TANGENT (remove the into-wall normal component) and rescale
 * the tangential vector back to the full intended step length. This slides the
 * character along the wall at full speed regardless of the wall's angle — the
 * axis-separated resolver only slid correctly along axis-aligned walls, which is
 * exactly why the ~24° Mouchao walls "jumped".
 *
 * - Fast path: destination clear → accept `to` unchanged (free movement identical).
 * - Escape-hatch: origin already inside a shape → allow full motion (matches
 *   axis-separated behaviour; lets a stuck spawn walk out).
 * - Up to 2 projections resolve a single wall then a corner (second wall).
 * - Fallback to axisSeparatedClamp if no free tangential position is found, so the
 *   fix is never worse than the pre-fix behaviour.
 *
 * The re-checked destination (`blocks(cx, cy)`) guarantees we never RETURN a
 * position inside a shape; per-tick step is a few px so the full-length rescale
 * cannot tunnel through a wall. Math.sqrt (not Math.hypot) keeps determinism.
 */
function slideAgainstShapes(
  from: Vec2,
  to: Vec2,
  radius: number,
  mapData: MapData,
  ring: { center: Vec2; radius: number } | null,
  blocks: (x: number, y: number) => boolean,
): Vec2 {
  // Destination already clear — nothing to resolve.
  if (!blocks(to.x, to.y)) return to;
  // Origin inside a shape — allow full motion so it can escape.
  if (blocks(from.x, from.y)) return to;

  const odx = to.x - from.x;
  const ody = to.y - from.y;
  const stepLen = Math.sqrt(odx * odx + ody * ody);
  if (stepLen === 0) return from;

  let dx = odx;
  let dy = ody;
  for (let iter = 0; iter < 2; iter++) {
    const n = collisionNormalAt(from.x + dx, from.y + dy, radius, mapData, ring);
    if (n === null) break;
    // Remove the component of motion heading INTO the wall (dot < 0). A
    // non-negative dot means the tangential motion already points away from the
    // wall, so there is nothing to strip.
    const dot = dx * n.x + dy * n.y;
    if (dot < 0) {
      dx -= dot * n.x;
      dy -= dot * n.y;
    }
    // Rescale the tangential motion to the full intended step — frictionless
    // slide generalised to arbitrary wall angles.
    const tLen = Math.sqrt(dx * dx + dy * dy);
    if (tLen <= 1e-9) break; // pure into-wall push, no tangent to slide along
    dx = (dx / tLen) * stepLen;
    dy = (dy / tLen) * stepLen;

    const cx = from.x + dx;
    const cy = from.y + dy;
    if (!blocks(cx, cy)) return { x: cx, y: cy };
  }

  // Could not find a free tangential slide (e.g. tight corner) — fall back to the
  // axis-separated clamp so behaviour is never worse than before the fix.
  return axisSeparatedClamp(from, to, blocks);
}

/**
 * Aggregate unit outward normal of every solid shape the character circle at
 * (x, y) currently overlaps (plinth ring, wall/obstacle polygons, fence
 * capsules, trunk ellipses). Individual shape normals are summed then normalised
 * so a corner (two walls) yields the bisector — tangent-projection against it
 * correctly refuses to slide deeper into the corner. Returns null when nothing
 * overlaps or the summed normal is degenerate. POLYGON MODE only (caller gates on
 * hasWalls); mirrors the shape set checked by the `blocks` closure.
 */
function collisionNormalAt(
  x: number,
  y: number,
  radius: number,
  mapData: MapData,
  ring: { center: Vec2; radius: number } | null,
): Vec2 | null {
  let nx = 0;
  let ny = 0;

  if (ring !== null && circleOverlapsRing(x, y, radius, ring.center, ring.radius)) {
    const dx = x - ring.center.x;
    const dy = y - ring.center.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 0) {
      nx += dx / len;
      ny += dy / len;
    }
  }

  // Boundary containment — when the circle has crossed the playable-area edge,
  // push it back toward the interior (inverse of a solid-wall normal) so the
  // slide resolver glides the player along the invisible boundary.
  if (mapData.boundary !== undefined && mapData.boundary.length >= 3) {
    if (!circleInsideBoundary(x, y, radius, mapData.boundary)) {
      const nrm = boundaryPushNormal(x, y, mapData.boundary);
      if (nrm !== null) {
        nx += nrm.x;
        ny += nrm.y;
      }
    }
  }

  for (const wall of mapData.walls ?? []) {
    if (circleOverlapsPolygon(x, y, radius, wall.vertices)) {
      const nrm = polygonPushNormal(x, y, wall.vertices);
      if (nrm !== null) {
        nx += nrm.x;
        ny += nrm.y;
      }
    }
  }

  for (const obs of mapData.obstacles ?? []) {
    if (circleOverlapsPolygon(x, y, radius, obs.vertices)) {
      const nrm = polygonPushNormal(x, y, obs.vertices);
      if (nrm !== null) {
        nx += nrm.x;
        ny += nrm.y;
      }
    }
  }

  for (const fence of mapData.fences ?? []) {
    const pts = fence.points;
    for (let i = 0; i + 1 < pts.length; i++) {
      if (
        circleOverlapsFence(
          x, y, radius,
          pts[i]!.x, pts[i]!.y,
          pts[i + 1]!.x, pts[i + 1]!.y,
          FENCE_COLLISION_HALF_WIDTH,
        )
      ) {
        const q = closestPointOnSegment(x, y, pts[i]!.x, pts[i]!.y, pts[i + 1]!.x, pts[i + 1]!.y);
        const dx = x - q.x;
        const dy = y - q.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0) {
          nx += dx / len;
          ny += dy / len;
        }
      }
    }
  }

  for (const tree of mapData.trees ?? []) {
    if (circleOverlapsTrunk(x, y, radius, tree.cx, tree.cy, tree.trunkRx, tree.trunkRy)) {
      const nrm = trunkPushNormal(x, y, tree.cx, tree.cy, tree.trunkRx, tree.trunkRy);
      if (nrm !== null) {
        nx += nrm.x;
        ny += nrm.y;
      }
    }
  }

  const len = Math.sqrt(nx * nx + ny * ny);
  if (len === 0) return null;
  return { x: nx / len, y: ny / len };
}

/**
 * Returns true iff a circle at (x, y) with the given radius would be blocked by
 * the polygon collision that the movement resolver enforces in POLYGON MODE
 * (Phase 08.1 — real Mouchao map with authored wall/obstacle/fence polygons).
 *
 * Checks:
 *   1. Boundary containment — circle must be fully inside the playable-area polygon
 *      (if `mapData.boundary` is present and has ≥3 vertices).
 *   2. Wall polygons (`mapData.walls`) — building walls at real authoring angles.
 *   3. Obstacle polygons (`mapData.obstacles`) — benches, urns, low walls, planters.
 *   4. Fence segments (`mapData.fences`) — stone walls, wire fences, hedges.
 *
 * Mirrors the exact guard set used by the `blocks` closure inside
 * `clampMotionAgainstWalls` (boundary + walls + obstacles + fences) so that a
 * spawn position accepted by this predicate is guaranteed movement-valid.
 *
 * Backward-compatible: when `mapData.walls` is absent/empty (minimal sim-test
 * MapData), all shape loops are no-ops and the function returns false — identical
 * to "no blocks" so tile-test fixtures are unaffected.
 *
 * Use `isPositionBlocked` to validate spawn positions (see GameScene
 * `snapToPassableTile`) so every spawned entity starts movement-valid, not
 * merely tile-grid-walkable.  Post-08.1 a tile the grid calls walkable can still
 * sit inside a building polygon → spawn frozen at round start (Phase 11 UAT bug).
 */
export function isPositionBlocked(
  x: number,
  y: number,
  radius: number,
  mapData: MapData,
): boolean {
  // 1. Boundary containment: the circle must be fully INSIDE the playable area.
  if (mapData.boundary !== undefined && mapData.boundary.length >= 3) {
    if (!circleInsideBoundary(x, y, radius, mapData.boundary)) return true;
  }

  // 2. Wall polygons — building walls at real authoring angles (~24° on Mouchao).
  for (const wall of mapData.walls ?? []) {
    if (circleOverlapsPolygon(x, y, radius, wall.vertices)) return true;
  }

  // 3. Obstacle polygons — benches, urns, low walls, planters.
  for (const obs of mapData.obstacles ?? []) {
    if (circleOverlapsPolygon(x, y, radius, obs.vertices)) return true;
  }

  // 4. Fence segments — stone walls, wire fences, hedges (capsule collision).
  for (const fence of mapData.fences ?? []) {
    const pts = fence.points;
    for (let i = 0; i + 1 < pts.length; i++) {
      if (
        circleOverlapsFence(
          x, y, radius,
          pts[i]!.x, pts[i]!.y,
          pts[i + 1]!.x, pts[i + 1]!.y,
          FENCE_COLLISION_HALF_WIDTH,
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Return the plinth ring's center + radius if the map has a 'plinth' zone,
 * else null. Inlined small helper so callers can compute once and reuse.
 */
export function getPlinthRing(mapData: MapData): { center: Vec2; radius: number } | null {
  const zone = mapData.zones.get('plinth');
  if (!zone) return null;
  return { center: zoneCenter(zone), radius: P3_PLINTH_RING_RADIUS_PX };
}

/**
 * True iff a character circle at (x, y) with radius `charRadius` overlaps
 * the solid plinth ring at `ringCenter` with radius `ringRadius`. Strict
 * less-than on the squared distance preserves the boundary-tangent semantic
 * (touching the ring is allowed; entering is not).
 */
export function circleOverlapsRing(
  x: number,
  y: number,
  charRadius: number,
  ringCenter: Vec2,
  ringRadius: number,
): boolean {
  const dx = x - ringCenter.x;
  const dy = y - ringCenter.y;
  const minDistSq = (charRadius + ringRadius) * (charRadius + ringRadius);
  return dx * dx + dy * dy < minDistSq;
}

/**
 * Circle-vs-rectangle overlap of the character circle against any non-walkable
 * tile in the bounding-box range. Uses the standard closest-point-on-rect test
 * (clamp the circle centre to the tile rectangle, compare squared distance
 * against radius²) so a circle that grazes a wall-tile CORNER is correctly
 * recognised as NOT overlapping when the geometric distance to the corner
 * exceeds the radius.
 *
 * Why circle-vs-rect (not AABB rasterise): debug session
 * .planning/debug/resolved/hunter-stuck-on-corner.md showed that the prior
 * AABB-vs-tile rasterise over-approximated the radius-16 circle as a 32×32
 * bbox, false-positively flagging a wall-corner overlap when the circle's
 * actual closest-point distance was √(15²+15²) ≈ 21.2 px — outside the radius.
 * Combined with A* `OnlyWhenNoObstacles` routing through corner geometry and
 * `aStarMoveVector` snapping to next-waypoint tile centres, this produced an
 * asymptotic stick-on-corner that read as "stuck forever" in live play.
 *
 * Boundary semantic preserved: a circle whose right edge is EXACTLY on a tile
 * boundary k*tileWidth still does NOT count as overlapping tile k (uses `<`,
 * not `<=`, on the squared-distance comparison). This keeps characters able
 * to walk right up against a wall without the stuck-detection at the origin
 * tripping the strict-blocking clamp into a permanent no-op.
 */
function circleOverlapsWall(
  x: number,
  y: number,
  radius: number,
  mapData: MapData,
): boolean {
  // AABB-of-circle establishes the candidate tile range. The closest-point
  // test inside the loop then rejects tiles the circle merely brushes by
  // bounding box but doesn't actually overlap.
  const leftTile = Math.floor((x - radius) / mapData.tileWidth);
  const rightTile = Math.ceil((x + radius) / mapData.tileWidth) - 1;
  const topTile = Math.floor((y - radius) / mapData.tileHeight);
  const bottomTile = Math.ceil((y + radius) / mapData.tileHeight) - 1;
  const radiusSq = radius * radius;
  for (let ty = topTile; ty <= bottomTile; ty++) {
    for (let tx = leftTile; tx <= rightTile; tx++) {
      // Phase 08.1 Item 2: use movementGrid (walls + obstacles + fences) for wall
      // clamping so obstacles/fences block character movement. Fall back to the
      // sight grid if movementGrid is absent (sim-test minimal MapData).
      if (isWalkable(mapData.movementGrid ?? mapData.grid, tx, ty)) continue;
      // Closest point on tile rectangle to the circle centre. Standard form:
      // clamp each axis independently to [tileMin, tileMax]. Squared distance
      // to that point compared against radius² gives true circle-vs-rect.
      const tileLeft = tx * mapData.tileWidth;
      const tileRight = (tx + 1) * mapData.tileWidth;
      const tileTop = ty * mapData.tileHeight;
      const tileBottom = (ty + 1) * mapData.tileHeight;
      const closestX = x < tileLeft ? tileLeft : x > tileRight ? tileRight : x;
      const closestY = y < tileTop ? tileTop : y > tileBottom ? tileBottom : y;
      const dx = x - closestX;
      const dy = y - closestY;
      if (dx * dx + dy * dy < radiusSq) return true;
    }
  }
  return false;
}
